import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchApplicationById,
  clearCurrentApplication,
} from '../../store/applicationSlice';
import { projectAPI } from '../../api/services';

interface Project {
  _id: string;
  name: string;
  description: string;
  status: string;
  priority: string;
  visibility: string;
  tags: string[];
  owner: {
    name: string;
  };
  stats: {
    totalTasks: number;
    completedTasks: number;
  };
  createdAt: string;
}

const ApplicationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { currentApplication, loading: applicationLoading } = useAppSelector((state) => state.applications);
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = React.useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchApplicationById({ id, includeProjects: false }));
      loadProjects();
    }

    return () => {
      dispatch(clearCurrentApplication());
    };
  }, [dispatch, id]);

  const loadProjects = async () => {
    if (!id) return;

    setProjectsLoading(true);
    try {
      const response = await projectAPI.getProjectsByApplication(id);
      setProjects(response.data.data || []);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setProjectsLoading(false);
    }
  };

  const getApplicationTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      backend: 'Backend',
      frontend_web: 'Frontend Web',
      mobile_ios: 'Mobile iOS',
      mobile_android: 'Mobile Android',
      desktop: 'Desktop',
      service: 'Service',
      library: 'Library',
      other: 'Other',
    };
    return labels[type] || type;
  };

  const getApplicationTypeColor = (type: string) => {
    const colors: { [key: string]: string } = {
      backend: 'bg-purple-100 text-purple-800',
      frontend_web: 'bg-blue-100 text-blue-800',
      mobile_ios: 'bg-pink-100 text-pink-800',
      mobile_android: 'bg-green-100 text-green-800',
      desktop: 'bg-indigo-100 text-indigo-800',
      service: 'bg-yellow-100 text-yellow-800',
      library: 'bg-orange-100 text-orange-800',
      other: 'bg-gray-100 text-gray-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (applicationLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading application...</p>
        </div>
      </div>
    );
  }

  if (!currentApplication) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          Application not found
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Application Header */}
      <div className="mb-8">
        <button
          onClick={() => {
            // Navigate back to the product detail if we have productIds
            if (currentApplication.productIds && currentApplication.productIds.length > 0) {
              navigate(`/admin/products/${currentApplication.productIds[0]}`);
            } else {
              navigate('/admin/products');
            }
          }}
          className="mb-4 text-blue-600 hover:text-blue-800 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Product
        </button>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-gray-900">{currentApplication.name}</h1>
                <span className={`px-3 py-1 text-sm rounded-full ${getApplicationTypeColor(currentApplication.applicationType)}`}>
                  {getApplicationTypeLabel(currentApplication.applicationType)}
                </span>
              </div>
              <p className="text-gray-600">{currentApplication.description}</p>
            </div>
            <span className={`px-3 py-1 text-sm rounded-full ${
              currentApplication.visibility === 'public' ? 'bg-green-100 text-green-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {currentApplication.visibility}
            </span>
          </div>

          {currentApplication.version && (
            <div className="mb-4">
              <span className="text-sm font-medium text-gray-700">Version: </span>
              <span className="text-sm text-gray-600">{currentApplication.version}</span>
            </div>
          )}

          {currentApplication.techStack && currentApplication.techStack.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Tech Stack:</p>
              <div className="flex flex-wrap gap-2">
                {currentApplication.techStack.map((tech, idx) => (
                  <span key={idx} className="px-3 py-1 bg-purple-50 text-purple-700 text-sm rounded">
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          )}

          {currentApplication.tags && currentApplication.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {currentApplication.tags.map((tag, idx) => (
                <span key={idx} className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded">
                  {tag}
                </span>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <p className="text-lg font-semibold text-gray-900 capitalize">{currentApplication.status}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Projects</p>
              <p className="text-lg font-semibold text-gray-900">{currentApplication.stats?.totalProjects || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Active Bugs</p>
              <p className="text-lg font-semibold text-gray-900">{currentApplication.stats?.activeBugs || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Owner</p>
              <p className="text-lg font-semibold text-gray-900">{currentApplication.owner.name}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Projects Section */}
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Projects</h2>
          <button
            onClick={() => {
              const productId = currentApplication.productIds && currentApplication.productIds.length > 0
                ? currentApplication.productIds[0]
                : '';
              navigate(`/admin/projects/new?applicationId=${id}&productId=${productId}`);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            + Create Project
          </button>
        </div>

        {projectsLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading projects...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.length === 0 ? (
              <div className="col-span-full text-center py-12 bg-white rounded-lg shadow">
                <p className="text-gray-500">No projects yet. Create one to get started.</p>
              </div>
            ) : (
              projects.map((project) => (
                <div
                  key={project._id}
                  className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-6"
                >
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-semibold text-gray-900">{project.name}</h3>
                    <div className="flex flex-col gap-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        project.status === 'completed' ? 'bg-green-100 text-green-800' :
                        project.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                        project.status === 'planning' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {project.status}
                      </span>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        project.priority === 'critical' ? 'bg-red-100 text-red-800' :
                        project.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                        project.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {project.priority}
                      </span>
                    </div>
                  </div>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{project.description}</p>

                  {project.tags && project.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.tags.map((tag, idx) => (
                        <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="mb-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Tasks:</span> {project.stats?.totalTasks || 0}
                      </div>
                      <div>
                        <span className="font-medium">Completed:</span> {project.stats?.completedTasks || 0}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                    <div className="text-sm text-gray-500">
                      <span className="font-medium">{project.owner.name}</span>
                    </div>
                    <button
                      onClick={() => navigate(`/employee/projects/${project._id}`)}
                      className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationDetail;
